/* Exact, receipt-backed display image replacement. Product, configuration and cart data stay unchanged. */
(() => {
  'use strict';
  const script = document.currentScript || document.querySelector('script[data-kalia-brand-images][data-map-url]');
  if (window.KaliaBrandImages) return;
  const ACCOUNT = 'nERRffKFW813-Rl-Ln6q8Q';
  const STORE = '/s/files/1/0728/7422/9857/files/';
  const ALIAS = '/cdn/shop/files/';
  const HOSTS = new Set(['kaliashades.com', 'www.kaliashades.com', 'sp20uq-sf.myshopify.com']);
  const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/;
  const VARIANTS = new Set(['large', 'thumbnail']);
  const DATA = ['data-zoom-src', 'data-gallery-image', 'data-hw-image', 'data-photo-url', 'data-photo-source-url'];
  const WATCH = ['src', 'srcset', 'data-src', 'data-srcset', 'data-original', 'href', 'style', ...DATA];
  const SELECTOR = ['img', 'picture source', 'a[href]', '[style]', ...DATA.map(name => `[${name}]`)].join(',');
  let cf = new Map(), native = new Map(), targets = new Map(), state = 'loading', count = 0;
  const disabled = new Set(), fallback = new Map(), records = new Map(), cssRecords = new Set();
  const pending = new Set();
  let scheduled = false, stylesQueued = false;

  function parts(value) {
    if (typeof value !== 'string' || !value || value !== value.trim() || value.includes('\\')) return null;
    try {
      const url = new URL(value, document.baseURI || location.href);
      return url.protocol === 'https:' && !url.username && !url.password && !url.port && !url.hash ? url : null;
    } catch (_) { return null; }
  }
  function safeQuery(url) {
    const seen = new Set();
    for (const [name, value] of url.searchParams) {
      if (seen.has(name)) return false;
      seen.add(name);
      if (name === 'v' && /^\d{1,20}$/.test(value)) continue;
      if (name === 'width' && /^[1-9]\d{0,4}$/.test(value) && Number(value) <= 10000) continue;
      if (name === 'format' && /^(?:jpg|jpeg|pjpg|png|webp|avif)$/.test(value)) continue;
      return false;
    }
    return true;
  }
  function validDelivery(value, id, variant) {
    const url = parts(value);
    return typeof value === 'string' && value.startsWith('https://') && url?.hostname === 'imagedelivery.net' && !url.search && url.pathname === `/${ACCOUNT}/${id}/${variant}`;
  }
  function validate(data) {
    if (data?.schema !== 'kalia.brand-images.v1' || data.accountHash !== ACCOUNT || data.nativeStorePath !== STORE || !Array.isArray(data.cloudflare) || !Array.isArray(data.shopify) || data.cloudflare.length + data.shopify.length > 10000) throw new Error('InvalidManifest');
    const nextCf = new Map(), nextNative = new Map(), nextTargets = new Map();
    const oldIds = new Set(data.cloudflare.map(row => row?.sourceId));
    function target(row) {
      if (!row || row.receiptConfirmed !== true || !UUID.test(row.targetId || '') || oldIds.has(row.targetId) || !VARIANTS.has(row.targetVariant) || !row.deliveryURLs || typeof row.deliveryURLs !== 'object' || Array.isArray(row.deliveryURLs)) throw new Error('UnconfirmedReceipt');
      const entries = Object.entries(row.deliveryURLs);
      if (!entries.length || entries.some(([variant, url]) => !VARIANTS.has(variant) || !validDelivery(url, row.targetId, variant)) || !Object.hasOwn(row.deliveryURLs, row.targetVariant)) throw new Error('InvalidDelivery');
      const url = row.deliveryURLs[row.targetVariant];
      const existing = nextTargets.get(row.targetId);
      // Aliases for the same uploaded bytes must agree on every confirmed delivery variant.
      if (existing && [...VARIANTS].some(variant => existing.deliveryURLs[variant] !== row.deliveryURLs[variant])) throw new Error('ConflictingTarget');
      nextTargets.set(row.targetId, { target: url, deliveryURLs: { ...row.deliveryURLs } });
      return url;
    }
    for (const row of data.cloudflare) {
      if (!row || !UUID.test(row.sourceId || '') || nextCf.has(row.sourceId) || !Array.isArray(row.sourceVariants) || !row.sourceVariants.length || new Set(row.sourceVariants).size !== row.sourceVariants.length || row.sourceVariants.some(value => !VARIANTS.has(value))) throw new Error('InvalidSource');
      nextCf.set(row.sourceId, { target: target(row), deliveryURLs: { ...row.deliveryURLs }, variants: new Set(row.sourceVariants) });
    }
    for (const row of data.shopify) {
      if (!row || typeof row.filename !== 'string' || !/^[a-z0-9][a-z0-9_.-]+\.(?:jpe?g|png|webp|avif)$/i.test(row.filename) || nextNative.has(row.filename)) throw new Error('InvalidNativeSource');
      nextNative.set(row.filename, { target: target(row), deliveryURLs: { ...row.deliveryURLs } });
    }
    return { cf: nextCf, native: nextNative, targets: nextTargets };
  }
  function resolve(value) {
    if (state !== 'ready' || typeof value !== 'string') return value;
    const url = parts(value);
    if (!url || !safeQuery(url)) return value;
    let row, chosen;
    if (url.hostname === 'imagedelivery.net') {
      const segments = url.pathname.split('/');
      if (segments.length !== 4 || segments[1] !== ACCOUNT || !VARIANTS.has(segments[3])) return value;
      row = cf.get(segments[2]);
      if (row && !row.variants.has(segments[3])) return value;
      if (row) chosen = row.deliveryURLs[segments[3]] || row.target;
      if (!row) {
        const current = targets.get(segments[2]);
        // A later thumbnail helper may request an unconfirmed variant of a NEW mapped ID.
        // Stay on an explicitly confirmed URL instead of inventing that delivery variant.
        if (!current) return value;
        const confirmed = current.deliveryURLs[segments[3]] || current.target;
        return disabled.has(confirmed) ? fallback.get(confirmed) || value : confirmed;
      }
    } else if (url.hostname === 'cdn.shopify.com' && url.pathname.startsWith(STORE)) {
      row = native.get(url.pathname.slice(STORE.length));
    } else if (HOSTS.has(url.hostname) && url.pathname.startsWith(ALIAS)) {
      row = native.get(url.pathname.slice(ALIAS.length));
    }
    if (!row) return value;
    if (!chosen) {
      const width = url.searchParams.has('width') ? Number(url.searchParams.get('width')) : 0;
      chosen = row.deliveryURLs[width > 0 && width <= 400 ? 'thumbnail' : 'large'] || row.target;
    }
    if (disabled.has(chosen)) return value;
    if (!fallback.has(chosen)) fallback.set(chosen, value);
    return chosen;
  }
  function srcset(value) {
    if (typeof value !== 'string' || !value || /(?:^|,)\s*data:/i.test(value)) return value;
    const candidates = value.split(',');
    // Preserve descriptors and whitespace. Fail closed on nonstandard/ambiguous lists.
    if (candidates.some(candidate => !/^\s*\S+(?:\s+(?:[1-9]\d*w|(?:\d+(?:\.\d+)?|\.\d+)x))?\s*$/.test(candidate))) return value;
    return candidates.map(candidate => candidate.replace(/\S+/, original => resolve(original))).join(',');
  }
  function css(value) {
    if (typeof value !== 'string' || !value.includes('url(')) return value;
    return value.replace(/url\(\s*(?:"([^"\\]*)"|'([^'\\]*)'|([^\s)'"\\]+))\s*\)/gi, (match, a, b, c) => {
      const original = a ?? b ?? c, next = resolve(original);
      return next === original ? match : `url("${next}")`;
    });
  }
  function exactTargets(value) {
    const result = [];
    for (const row of targets.values()) for (const url of Object.values(row.deliveryURLs)) if (value.includes(url) && !result.includes(url)) result.push(url);
    return result;
  }
  function overlay() { window.KaliaSampleBrand?.refresh?.(); }
  function restoreTarget(url) {
    disabled.add(url);
    diagnostics();
    for (const [node, changes] of records) {
      if (!node.isConnected) { records.delete(node); continue; }
      for (const [attribute, change] of changes) {
        if (!change.targets.includes(url)) continue;
        if (node.getAttribute(attribute) === change.after) node.setAttribute(attribute, change.before);
        changes.delete(attribute);
        if (node.tagName === 'IMG') node.dataset.kaliaBrandImageState = 'fallback';
      }
      if (!changes.size) records.delete(node);
    }
    for (const record of cssRecords) if (record.targets.includes(url)) {
      if (record.style.getPropertyValue('background-image') === record.after) record.style.setProperty('background-image', record.before, record.priority);
      cssRecords.delete(record);
    }
    overlay();
  }
  function setAttribute(node, attribute, before, after) {
    if (after === before || !node.isConnected || node.getAttribute(attribute) !== before) return;
    const used = exactTargets(after);
    if (!used.length) {
      if (exactTargets(before).some(url => disabled.has(url))) node.setAttribute(attribute, after);
      return;
    }
    const changes = records.get(node) || new Map();
    const previous = changes.get(attribute);
    changes.set(attribute, { before: previous?.after === before ? previous.before : before, after, targets: used });
    records.set(node, changes);
    // Preserve native loading=lazy, sizes and responsive browser selection. No probing/preloading.
    node.setAttribute(attribute, after);
    if (node.tagName === 'IMG') node.dataset.kaliaBrandImageState = 'loading';
  }
  function setBackground(style) {
    const before = style.getPropertyValue('background-image');
    const after = css(before);
    if (after === before) return;
    const used = exactTargets(after), priority = style.getPropertyPriority('background-image');
    cssRecords.add({ style, before, after, priority, targets: used });
    style.setProperty('background-image', after, priority);
  }
  function enhance(node) {
    if (state !== 'ready' || node.nodeType !== 1) return;
    const tag = node.tagName;
    const attributes = [...DATA];
    if (tag === 'IMG') attributes.push('src', 'data-src', 'data-original');
    if (tag === 'IMG' || tag === 'SOURCE' && node.parentElement?.tagName === 'PICTURE') attributes.push('srcset', 'data-srcset');
    // Only an exact known image URL qualifies; ordinary product, policy and supplier links never do.
    if (tag === 'A') attributes.push('href');
    for (const attribute of attributes) {
      if (!node.hasAttribute(attribute)) continue;
      const before = node.getAttribute(attribute);
      setAttribute(node, attribute, before, attribute.endsWith('srcset') ? srcset(before) : resolve(before));
    }
    if (node.style?.getPropertyValue('background-image')) setBackground(node.style);
  }
  function scan(root = document) {
    if (state !== 'ready') return;
    if (root.nodeType === 1 && root.matches(SELECTOR)) enhance(root);
    root.querySelectorAll?.(SELECTOR).forEach(enhance);
    for (const node of records.keys()) if (!node.isConnected) records.delete(node);
  }
  function styles() {
    if (state !== 'ready') return;
    const walk = rules => {
      for (const rule of rules) {
        if (rule.style?.getPropertyValue('background-image')) setBackground(rule.style);
        if (rule.cssRules) walk(rule.cssRules);
      }
    };
    for (const sheet of document.styleSheets) {
      try { walk(sheet.cssRules); } catch (_) { /* Cross-origin CSSOM access is deliberately not bypassed. */ }
    }
  }
  function queue(node) {
    pending.add(node);
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      const nodes = [...pending]; pending.clear();
      nodes.forEach(scan);
      if (stylesQueued) { stylesQueued = false; styles(); }
    });
  }
  const observer = new MutationObserver(mutations => {
    if (state !== 'ready') return;
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') queue(mutation.target);
      else {
        for (const node of mutation.addedNodes) if (node.nodeType === 1) { queue(node); if (node.tagName === 'STYLE' || node.querySelector?.('style')) stylesQueued = true; }
        if (mutation.target.nodeName === 'STYLE') { stylesQueued = true; queue(document); }
      }
    }
  });
  if (document.documentElement) observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: WATCH });
  document.addEventListener('load', event => {
    if (event.target.tagName === 'LINK' && event.target.rel === 'stylesheet') styles();
    if (event.target.tagName === 'IMG') {
      const current = event.target.currentSrc || event.target.getAttribute('src') || '';
      if (exactTargets(current).some(url => current === url && !disabled.has(url))) event.target.dataset.kaliaBrandImageState = 'ready';
    }
  }, true);
  document.addEventListener('error', event => {
    const image = event.target;
    if (image.tagName !== 'IMG' || state !== 'ready') return;
    const current = image.currentSrc || image.getAttribute('src') || '';
    const url = exactTargets(current).find(value => current === value);
    if (!url) return;
    const original = fallback.get(url);
    if (!disabled.has(url)) restoreTarget(url);
    // A zoom may have read an already-remapped data source before creating its image.
    if (original && image.getAttribute('src') === url) {
      image.removeAttribute('srcset'); image.removeAttribute('sizes'); image.setAttribute('src', original);
    }
    image.dataset.kaliaBrandImageState = 'fallback';
    // Let this exact original source retry before a component's own error handler hides it.
    // Errors from the original/unknown image still reach that component normally.
    if (original) event.stopImmediatePropagation();
  }, true);
  document.addEventListener('DOMContentLoaded', () => { scan(); styles(); }, { once: true });
  document.addEventListener('shopify:section:load', event => { scan(event.target); styles(); });

  function mapURL(value) {
    const url = parts(value);
    if (!url || !url.pathname.endsWith('/kalia-brand-images-map.json')) return null;
    const owned = HOSTS.has(url.hostname) && /^\/cdn\/shop\/(?:t\/\d+\/assets|files)\//.test(url.pathname);
    const cdn = url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/1/0728/7422/9857/') && /\/(?:assets|files)\//.test(url.pathname);
    return owned || cdn ? url.href : null;
  }
  function diagnostics() {
    if (script?.dataset) { script.dataset.brandImagesCount = String(count); script.dataset.failedImageCount = String(disabled.size); }
  }
  function mark(value, error = '') {
    state = value;
    diagnostics();
    if (script?.dataset) { script.dataset.brandImagesState = value; if (error) script.dataset.brandImagesError = error; }
  }
  let finishReady;
  const ready = new Promise(resolveReady => { finishReady = resolveReady; });
  window.KaliaBrandImages = Object.freeze({ resolve, resolveSrcset: srcset, resolveCss: css, refresh: scan, ready,
    get state() { return state; }, get count() { return count; }, get failedImageCount() { return disabled.size; } });
  async function load() {
    mark('loading');
    const url = mapURL(script?.dataset.mapUrl);
    if (!url) { mark('failed', 'InvalidMapURL'); finishReady({ state, count }); return; }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, { credentials: 'omit', mode: 'cors', cache: 'force-cache', signal: controller.signal });
      if (!response.ok || response.url && !mapURL(response.url) || Number(response.headers?.get?.('content-length') || 0) > 2000000) throw new Error('MapResponse');
      const text = await response.text();
      if (text.length > 2000000) throw new Error('MapTooLarge');
      const result = validate(JSON.parse(text));
      cf = result.cf; native = result.native; targets = result.targets; count = cf.size + native.size;
      mark(count ? 'ready' : 'empty');
      scan(); styles();
    } catch (error) {
      mark('failed', /^[a-zA-Z]{1,40}$/.test(error?.message || '') ? error.message : 'MapLoad');
    } finally {
      clearTimeout(timeout);
      finishReady({ state, count });
      document.dispatchEvent(new CustomEvent('kalia:brand-images:ready', { detail: { state, count } }));
    }
  }
  void load();
})();
